import apiClient from "../../../helpers/apiClient";
import { getMissingInsuranceDetails } from "../../insurance/lib/insuranceSaleApi";

const PLATFORM_INSURANCE_TYPES = ["teqmobility", "bonzah", "abi"];

const fetchAddons = async (addonsArray) => {
    try {
        const { data } = await apiClient.get("/api/addon/list", {
            params: {
                id: addonsArray,
                type: "insurance",
            },
        });

        if (data?.data) {
            return data.data.result || [];
        }
        return [];
    } catch {
        return [];
    }
};

export const fetchCustomerByEmail = async (email) => {
    if (!email) return null;

    try {
        const { data } = await apiClient.get(`/api/user/list?email=${encodeURIComponent(email)}`);
        if (data?.success && data?.data?.result?.[0]) {
            return data.data.result[0];
        }
    } catch {
        // ignore
    }

    return null;
};

const fetchDriverApplicantId = async (userEmail) => {
    const user = await fetchCustomerByEmail(userEmail);
    return user?.drivingLicense?.driverApplicantId ?? null;
};

const checkVerificationStatus = async (driverApplicantId) => {
    try {
        const personalStatus = await apiClient.get(`/api/driver/realtime-verification/PERSONAL/${driverApplicantId}`);
        const rideshareStatus = await apiClient.get(
            `/api/driver/realtime-verification/RIDESHARE/${driverApplicantId}`,
        );

        const personalPassedStatus = personalStatus.data.data?.data?.status === "PASSED";
        const professionalPassedStatus = rideshareStatus.data.data?.data?.status === "PASSED";

        if (personalStatus.data.success && rideshareStatus.data.success) {
            if (personalPassedStatus || professionalPassedStatus) {
                return "verified";
            }
            return "unverified";
        }
        return "unverified";
    } catch {
        return "unverified";
    }
};

const driverLicenseVerification = async (userEmail) => {
    try {
        const driverApplicantId = await fetchDriverApplicantId(userEmail);
        if (!driverApplicantId) {
            return "no_applicant_id";
        }
        return checkVerificationStatus(driverApplicantId);
    } catch {
        return "unverified";
    }
};

export async function reservationHasPlatformInsurance(reservation) {
    if (!reservation) return false;

    const insuranceType = reservation.insuranceType?.toLowerCase();
    if (insuranceType === "axle") {
        return false;
    }

    if (insuranceType && PLATFORM_INSURANCE_TYPES.includes(insuranceType)) {
        return true;
    }

    if (reservation.addons?.length > 0) {
        const insuranceAddons = await fetchAddons(reservation.addons);
        return insuranceAddons.length > 0;
    }

    return false;
}

export const getReservationInsuranceProvider = (reservation) => {
    const insuranceType = reservation?.insuranceType?.toLowerCase();
    if (insuranceType && PLATFORM_INSURANCE_TYPES.includes(insuranceType)) {
        return insuranceType;
    }
    return "teqmobility";
};

export const checkLicenseVerificationForReservation = async (reservation) => {
    const hasInsurance = await reservationHasPlatformInsurance(reservation);

    if (!hasInsurance) {
        return {
            hasInsurance: false,
            status: "not_required",
        };
    }

    const verificationStatus = await driverLicenseVerification(reservation.email);
    return {
        hasInsurance: true,
        status: verificationStatus,
    };
};

export async function resolveStartReservationGate(reservation, companyData) {
    const hasInsurance = await reservationHasPlatformInsurance(reservation);
    if (!hasInsurance) {
        return { canProceed: true };
    }

    const customer = await fetchCustomerByEmail(reservation.email);
    const provider = getReservationInsuranceProvider(reservation);
    const missingDetails = getMissingInsuranceDetails(customer, provider, companyData);
    const verificationStatus = await driverLicenseVerification(reservation.email);

    const verificationBlocked =
        verificationStatus === "unverified" || verificationStatus === "no_applicant_id";
    const profileIncomplete = !customer || missingDetails.length > 0;

    if (profileIncomplete || verificationBlocked) {
        return {
            canProceed: false,
            customer,
            provider,
            missingDetails,
            verificationStatus,
        };
    }

    return { canProceed: true };
}
